/**
 * TopUpModal — modal for selecting credit package + redirect to Stripe.
 *
 * A15-N6.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - reason: optional string (e.g., "Sie haben das Tageslimit erreicht")
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../lib/apiClient';
import { useTopUpProducts } from '../../hooks/useCredits';

export default function TopUpModal({ open, onClose, reason }) {
  const { t } = useTranslation();
  const { products, loading, error } = useTopUpProducts();
  const [purchasing, setPurchasing] = useState(false);

  if (!open) return null;

  async function handlePurchase(product) {
    setPurchasing(true);
    try {
      const res = await apiClient.post('/api/v1/aif/credits/topup', {
        product_id: product.id,
      });
      const data = res?.data ?? res;
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch {
      // silent — user sees no redirect
    } finally {
      setPurchasing(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      data-testid="topup-modal-overlay"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-bold dark:text-white" data-testid="topup-modal-title">
              {t('credits.topup_title', 'Credits aufladen')}
            </h2>
            {reason && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{reason}</p>}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl leading-none"
            aria-label="Close"
            data-testid="topup-modal-close"
          >
            &times;
          </button>
        </div>

        {loading && <p data-testid="topup-modal-loading">{t('common.loading', 'Loading...')}</p>}
        {error && (
          <div className="bg-red-100 dark:bg-red-900 p-3 rounded mb-4 text-[var(--aif-color-danger)]">
            {error}
          </div>
        )}

        {!loading && !error && (
          <div className="grid grid-cols-2 gap-4" data-testid="topup-products-grid">
            {products.map((product) => (
              <button
                key={product.id}
                onClick={() => handlePurchase(product)}
                disabled={purchasing}
                className="border-2 border-gray-200 dark:border-gray-600 hover:border-blue-500 rounded-lg p-4 text-left transition disabled:opacity-50"
                data-testid={`topup-product-${product.sku}`}
              >
                <div className="text-lg font-semibold dark:text-white">{product.display_name}</div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-2">
                  €{(product.price_eur_cents / 100).toFixed(2)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {product.credits_granted.toLocaleString('de-DE')} Credits
                </div>
                {product.description && (
                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                    {product.description}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 text-center">
          {t('credits.payment_info', 'Zahlung über Stripe. Sichere Verarbeitung. SEPA, Kreditkarte, Apple Pay verfügbar.')}
        </p>
      </div>
    </div>
  );
}
